/**
 * Image Format Conversion Utilities
 * Handles BMP, TIFF, and ICO encoding for browser-side conversion
 */

import UTIF from 'utif';

/**
 * Load TIFF file and convert to canvas
 */
export async function loadTiffToCanvas(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target.result;
        const ifds = UTIF.decode(buffer);
        
        if (!ifds || ifds.length === 0) {
          reject(new Error('Invalid TIFF file'));
          return;
        }
        
        // Decode the first page
        UTIF.decodeImage(buffer, ifds[0]);
        const firstPage = ifds[0];
        
        const width = firstPage.width;
        const height = firstPage.height;
        const rgba = UTIF.toRGBA8(firstPage);
        
        // Create canvas and draw the image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);
        
        resolve({ canvas, width, height });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Convert canvas to BMP format
 * BMP is uncompressed, so this creates a raw pixel data file
 */
export function canvasToBMP(canvas) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  
  const width = canvas.width;
  const height = canvas.height;
  
  // BMP row padding - rows must be multiple of 4 bytes
  const rowSize = Math.ceil((width * 3) / 4) * 4;
  const pixelArraySize = rowSize * height;
  const fileSize = 54 + pixelArraySize; // 54 bytes header + pixel data
  
  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);
  
  // BMP File Header (14 bytes)
  view.setUint8(0, 0x42); // 'B'
  view.setUint8(1, 0x4D); // 'M'
  view.setUint32(2, fileSize, true); // File size
  view.setUint32(6, 0, true); // Reserved
  view.setUint32(10, 54, true); // Pixel data offset
  
  // DIB Header (BITMAPINFOHEADER - 40 bytes)
  view.setUint32(14, 40, true); // DIB header size
  view.setInt32(18, width, true); // Width
  view.setInt32(22, -height, true); // Height (negative = top-down)
  view.setUint16(26, 1, true); // Color planes
  view.setUint16(28, 24, true); // Bits per pixel (24-bit RGB)
  view.setUint32(30, 0, true); // Compression (none)
  view.setUint32(34, pixelArraySize, true); // Image size
  view.setUint32(38, 2835, true); // Horizontal resolution (72 DPI)
  view.setUint32(42, 2835, true); // Vertical resolution (72 DPI)
  view.setUint32(46, 0, true); // Colors in palette
  view.setUint32(50, 0, true); // Important colors
  
  // Pixel data (BGR format, bottom-up by default but we use negative height for top-down)
  let offset = 54;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      view.setUint8(offset++, pixels[i + 2]); // Blue
      view.setUint8(offset++, pixels[i + 1]); // Green
      view.setUint8(offset++, pixels[i]);     // Red
    }
    // Add padding to make row multiple of 4 bytes
    const padding = rowSize - (width * 3);
    for (let p = 0; p < padding; p++) {
      view.setUint8(offset++, 0);
    }
  }
  
  return new Blob([buffer], { type: 'image/bmp' });
}

/**
 * Convert canvas to TIFF format
 * Creates a simple uncompressed TIFF
 */
export function canvasToTIFF(canvas) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  
  const width = canvas.width;
  const height = canvas.height;
  const samplesPerPixel = 3; // RGB
  const bitsPerSample = 8;
  
  // Calculate sizes
  const stripSize = width * height * samplesPerPixel;
  const ifdOffset = 8; // Right after header
  const numTags = 12;
  const ifdSize = 2 + (numTags * 12) + 4; // count + tags + next IFD pointer
  const stripOffset = ifdOffset + ifdSize + 24; // IFD + extra data
  const fileSize = stripOffset + stripSize;
  
  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);
  let offset = 0;
  
  // TIFF Header
  view.setUint16(offset, 0x4949, true); offset += 2; // Little endian 'II'
  view.setUint16(offset, 42, true); offset += 2; // TIFF magic number
  view.setUint32(offset, ifdOffset, true); offset += 4; // IFD offset
  
  // IFD (Image File Directory)
  view.setUint16(offset, numTags, true); offset += 2; // Number of directory entries
  
  // Helper function to write IFD entry
  const writeTag = (tag, type, count, value) => {
    view.setUint16(offset, tag, true); offset += 2;
    view.setUint16(offset, type, true); offset += 2;
    view.setUint32(offset, count, true); offset += 4;
    if (type === 3 && count === 1) { // SHORT
      view.setUint16(offset, value, true); offset += 2;
      view.setUint16(offset, 0, true); offset += 2;
    } else if (type === 4 && count === 1) { // LONG
      view.setUint32(offset, value, true); offset += 4;
    } else {
      view.setUint32(offset, value, true); offset += 4; // Offset to data
    }
  };
  
  // Required TIFF tags
  writeTag(256, 4, 1, width);              // ImageWidth
  writeTag(257, 4, 1, height);             // ImageLength
  writeTag(258, 3, 3, ifdOffset + ifdSize); // BitsPerSample (offset to data)
  writeTag(259, 3, 1, 1);                  // Compression (none)
  writeTag(262, 3, 1, 2);                  // PhotometricInterpretation (RGB)
  writeTag(273, 4, 1, stripOffset);        // StripOffsets
  writeTag(277, 3, 1, samplesPerPixel);    // SamplesPerPixel
  writeTag(278, 4, 1, height);             // RowsPerStrip
  writeTag(279, 4, 1, stripSize);          // StripByteCounts
  writeTag(282, 5, 1, ifdOffset + ifdSize + 6);  // XResolution (offset)
  writeTag(283, 5, 1, ifdOffset + ifdSize + 14); // YResolution (offset)
  writeTag(296, 3, 1, 2);                  // ResolutionUnit (inch)
  
  // Next IFD offset (0 = no more IFDs)
  view.setUint32(offset, 0, true); offset += 4;
  
  // BitsPerSample values (3 x 16-bit)
  view.setUint16(offset, 8, true); offset += 2;
  view.setUint16(offset, 8, true); offset += 2;
  view.setUint16(offset, 8, true); offset += 2;
  
  // XResolution (72/1)
  view.setUint32(offset, 72, true); offset += 4;
  view.setUint32(offset, 1, true); offset += 4;
  
  // YResolution (72/1)
  view.setUint32(offset, 72, true); offset += 4;
  view.setUint32(offset, 1, true); offset += 4;
  
  // Pixel data (RGB)
  offset = stripOffset;
  for (let i = 0; i < pixels.length; i += 4) {
    view.setUint8(offset++, pixels[i]);     // Red
    view.setUint8(offset++, pixels[i + 1]); // Green
    view.setUint8(offset++, pixels[i + 2]); // Blue
  }
  
  return new Blob([buffer], { type: 'image/tiff' });
}

/**
 * Convert canvas to ICO format
 * Creates a simple ICO with a single image
 */
export function canvasToICO(canvas, sizes = [32]) {
  const images = [];
  
  for (const size of sizes) {
    // Create resized canvas
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = size;
    resizedCanvas.height = size;
    const ctx = resizedCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0, size, size);
    
    const imageData = ctx.getImageData(0, 0, size, size);
    const pixels = imageData.data;
    
    // Create BMP-style image data (BGRA, bottom-up)
    const bmpData = [];
    for (let y = size - 1; y >= 0; y--) {
      for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4;
        bmpData.push(pixels[i + 2]); // Blue
        bmpData.push(pixels[i + 1]); // Green
        bmpData.push(pixels[i]);     // Red
        bmpData.push(pixels[i + 3]); // Alpha
      }
    }
    
    images.push({
      size: size,
      data: new Uint8Array(bmpData),
      bpp: 32
    });
  }
  
  // Calculate file size
  const headerSize = 6; // ICONDIR
  const dirEntrySize = 16 * images.length; // ICONDIRENTRY for each image
  let dataOffset = headerSize + dirEntrySize;
  
  // Calculate total size
  let totalSize = dataOffset;
  for (const img of images) {
    totalSize += 40 + img.data.length; // BITMAPINFOHEADER + pixel data
  }
  
  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  let offset = 0;
  
  // ICONDIR
  view.setUint16(offset, 0, true); offset += 2; // Reserved
  view.setUint16(offset, 1, true); offset += 2; // Type (1 = ICO)
  view.setUint16(offset, images.length, true); offset += 2; // Image count
  
  // Calculate offsets for each image
  let currentOffset = dataOffset;
  
  // ICONDIRENTRY for each image
  for (const img of images) {
    const imageSize = 40 + img.data.length;
    
    view.setUint8(offset++, img.size < 256 ? img.size : 0); // Width
    view.setUint8(offset++, img.size < 256 ? img.size : 0); // Height
    view.setUint8(offset++, 0); // Color palette
    view.setUint8(offset++, 0); // Reserved
    view.setUint16(offset, 1, true); offset += 2; // Color planes
    view.setUint16(offset, img.bpp, true); offset += 2; // Bits per pixel
    view.setUint32(offset, imageSize, true); offset += 4; // Image size
    view.setUint32(offset, currentOffset, true); offset += 4; // Image offset
    
    currentOffset += imageSize;
  }
  
  // Image data
  for (const img of images) {
    // BITMAPINFOHEADER
    view.setUint32(offset, 40, true); offset += 4; // Header size
    view.setInt32(offset, img.size, true); offset += 4; // Width
    view.setInt32(offset, img.size * 2, true); offset += 4; // Height (double for XOR + AND mask)
    view.setUint16(offset, 1, true); offset += 2; // Planes
    view.setUint16(offset, img.bpp, true); offset += 2; // Bits per pixel
    view.setUint32(offset, 0, true); offset += 4; // Compression
    view.setUint32(offset, img.data.length, true); offset += 4; // Image size
    view.setUint32(offset, 0, true); offset += 4; // X pixels per meter
    view.setUint32(offset, 0, true); offset += 4; // Y pixels per meter
    view.setUint32(offset, 0, true); offset += 4; // Colors used
    view.setUint32(offset, 0, true); offset += 4; // Important colors
    
    // Pixel data
    for (let i = 0; i < img.data.length; i++) {
      view.setUint8(offset++, img.data[i]);
    }
  }
  
  return new Blob([buffer], { type: 'image/x-icon' });
}

/**
 * Load image from file and draw to canvas
 * Handles special formats like TIFF that browsers don't natively support
 */
export async function loadImageToCanvas(file) {
  // Check if it's a TIFF file
  const isTiff = file.type === 'image/tiff' || 
                 file.name.toLowerCase().endsWith('.tiff') || 
                 file.name.toLowerCase().endsWith('.tif');
  
  if (isTiff) {
    return loadTiffToCanvas(file);
  }
  
  // Standard image loading for supported formats
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      // Fill with white for formats that don't support transparency
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      URL.revokeObjectURL(img.src);
      resolve({ canvas, width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Convert image file to target format
 */
export async function convertImage(file, targetFormat, options = {}) {
  const { canvas, width, height } = await loadImageToCanvas(file);
  
  let blob;
  let extension;
  
  switch (targetFormat.toLowerCase()) {
    case 'png':
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      extension = '.png';
      break;
    case 'jpg':
    case 'jpeg':
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', options.quality || 0.9));
      extension = targetFormat === 'jpeg' ? '.jpeg' : '.jpg';
      break;
    case 'webp':
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', options.quality || 0.9));
      extension = '.webp';
      break;
    case 'bmp':
      blob = canvasToBMP(canvas);
      extension = '.bmp';
      break;
    case 'tiff':
    case 'tif':
      blob = canvasToTIFF(canvas);
      extension = '.tiff';
      break;
    case 'ico':
      blob = canvasToICO(canvas, options.sizes || [32, 64]);
      extension = '.ico';
      break;
    default:
      throw new Error(`Unsupported format: ${targetFormat}`);
  }
  
  return {
    blob,
    extension,
    width,
    height
  };
}
